// Где ищут палитра ⌘K и страница поиска /search (T137; ux.md, раздел 8; contracts/ui.md, «Палитра
// ⌘K»): что ищется — по правам роли. Пользователи — по ФИО и почте (users.read); замещения — по ФИО
// владельца роли или заместителя (substitutions.read: администратору все, остальным свои — правило
// «в сеансе», поэтому искать есть что у любого вошедшего); временный доступ сопровождения — по ФИО
// сопровождения или выдавшего (support.read); журнал действий — по ФИО автора и названию записи
// (audit.read.*). Поиск — на сервере теми же запросами, что и у разделов, с двух букв. Сбой
// запроса — ошибка с причиной, кодом и номером операции, а не пустой результат (T135); на время
// «Повторить» она остаётся на месте с признаком загрузки (T168). Отказ в доступе (ACC-*, например
// журнал у сопровождения без временного доступа) — источник скрыт: искать там сейчас нечего.
//
// Протоколы и пункты (003, приёмка 0.3.1), люди и организации справочника (005, FR-034в; макет 8)
// — без временного доступа (readsDocuments, readsControl, readsDirectory): протоколы — по названию
// и П-N в списке документов, переход в «Контроль» по документу; пункты — поиском сервера по тексту,
// переход в «Контроль» на этот пункт; люди и организации — поиском справочника по ФИО, названию
// и псевдонимам, переход — карточка записи в «Справочнике».
import {
  CONTROL_SEARCH_MAX,
  DIRECTORY_SEARCH_LIMIT,
  formatOperation,
  roleInfo,
} from '@kp/contracts';
import type {
  ControlItem,
  DocumentRow,
  Operation,
  Permissions,
  RecordKind,
  RouteResponse,
} from '@kp/contracts';
import type { LucideIcon } from '@kp/ui';
import {
  Building2,
  ClipboardList,
  Files,
  LifeBuoy,
  Repeat2,
  ScrollText,
  UserRound,
} from '@kp/ui/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { NavigateOptions } from '@tanstack/react-router';
import { apiQuery, isApiError } from '../../api/client.ts';
import type { ErrorView } from '../../app/errors.ts';
import { formatDate, formatDateTime } from '../../app/format.ts';
import {
  auditActorName,
  auditOperationLabel,
  entityTypeLabel,
  substitutionStateLabels,
  supportGrantStateLabels,
} from '../../app/labels.ts';
import {
  can,
  isForbiddenCode,
  readsControl,
  readsDirectory,
  readsDocuments,
  searchSourceOperations,
} from '../../shell/permissions.ts';
import { useMe, useTimeZone } from '../../shell/session.ts';
import { useQueryFailure } from '../load-error.tsx';

/** Поиск — с двух букв (FR-037). */
export const SEARCH_FROM = 2;

export type SearchSourceId =
  | 'documents'
  | 'people'
  | 'organizations'
  | 'items'
  | 'users'
  | 'substitutions'
  | 'support'
  | 'audit';

interface SourceInfo {
  id: SearchSourceId;
  /** Заголовок группы в палитре и раздела на странице поиска — название раздела меню. */
  heading: string;
  icon: LucideIcon;
  operations: readonly Operation[];
  /**
   * Право на источник сверх операций: протоколы, пункты и справочник — без временного доступа
   * (сопровождению без него сервер отвечает ACC-003).
   */
  allows?: (permissions: Permissions) => boolean;
  /** «Не удалось найти {что}» — в строке ошибки. */
  what: string;
}

// Люди — выше пунктов (макет 8 005): человека ищут по фамилии чаще, чем пункт по его тексту.
export const searchSources: readonly SourceInfo[] = [
  {
    id: 'documents',
    heading: 'Протоколы',
    icon: Files,
    operations: ['documents.read'],
    allows: readsDocuments,
    what: 'протоколы',
  },
  {
    id: 'people',
    heading: 'Люди',
    icon: UserRound,
    operations: ['directory.read'],
    allows: readsDirectory,
    what: 'людей',
  },
  {
    id: 'organizations',
    heading: 'Организации',
    icon: Building2,
    operations: ['directory.read'],
    allows: readsDirectory,
    what: 'организации',
  },
  {
    id: 'items',
    heading: 'Пункты',
    icon: ClipboardList,
    operations: ['control.read'],
    allows: readsControl,
    what: 'пункты',
  },
  {
    id: 'users',
    heading: 'Пользователи',
    icon: UserRound,
    operations: searchSourceOperations.users,
    what: 'пользователей',
  },
  {
    id: 'substitutions',
    heading: 'Замещения',
    icon: Repeat2,
    operations: searchSourceOperations.substitutions,
    what: 'замещения',
  },
  {
    id: 'support',
    heading: 'Доступ сопровождения',
    icon: LifeBuoy,
    operations: searchSourceOperations.support,
    what: 'выдачи доступа',
  },
  {
    id: 'audit',
    heading: 'Журнал действий',
    icon: ScrollText,
    operations: searchSourceOperations.audit,
    what: 'записи журнала',
  },
];

/** Где искать этому пользователю — по правам ролей. */
export function allowedSources(permissions: Permissions): SourceInfo[] {
  return searchSources.filter((source) =>
    source.allows ? source.allows(permissions) : can(permissions, source.operations),
  );
}

/** «ФИО, почту, название протокола, текст пункта» — что ищется у этого пользователя. */
function searchedFields(permissions: Permissions): string[] {
  return [
    'ФИО',
    can(permissions, ['users.read']) ? 'почту' : null,
    readsDocuments(permissions) ? 'название протокола' : null,
    readsControl(permissions) ? 'текст пункта' : null,
  ].filter((part): part is string => part !== null);
}

/** «ФИО, почту или текст пункта» — последнее через «или». */
function joinOr(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} или ${parts[parts.length - 1]}`;
}

/** Подсказка поля поиска: почта — только если ищутся пользователи. */
export function searchPlaceholder(permissions: Permissions): string {
  if (readsDocuments(permissions) || readsControl(permissions)) {
    return readsDirectory(permissions)
      ? 'Найдите протокол, пункт, человека или запись…'
      : 'Найдите протокол, пункт или запись…';
  }
  return can(permissions, ['users.read'])
    ? 'Найдите по ФИО, почте или названию записи…'
    : 'Найдите по ФИО или названию записи…';
}

/** Пока введено меньше двух букв. */
export function searchHint(permissions: Permissions): string {
  return `Введите ${joinOr(searchedFields(permissions))} — от двух букв`;
}

/**
 * Протокол по вводу: «П-12», «п 12», «12» — номер П-N; иначе — часть названия без учёта
 * регистра и ё: «опер» → «Протокол оперативного совещания».
 */
export function matchesDocument(
  row: Pick<DocumentRow, 'number' | 'title'>,
  query: string,
): boolean {
  const fold = (text: string) => text.toLocaleLowerCase('ru-RU').replaceAll('ё', 'е').trim();
  const needle = fold(query);
  if (!needle) return false;
  const number = /^п?[\s-]*(\d+)$/.exec(needle)?.[1];
  if (number !== undefined && Number(number) === row.number) return true;
  return fold(row.title).includes(needle);
}

/** «П-3 · Производство работ / Жилой дом № 1.6 · п. 12» — откуда пункт. */
export function itemSource(
  item: Pick<ControlItem, 'documentNumber' | 'section' | 'number'>,
): string {
  return [`П-${item.documentNumber}`, item.section, item.number ? `п. ${item.number}` : null]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

type DirectoryHit = RouteResponse<'directory.search'>['items'][number];

/**
 * Пояснение строки человека или организации: место работы и псевдоним, по которому нашлось —
 * «Юрлицо 1 · по написанию «Горбунов СБ»».
 */
export function directoryHitSub(hit: DirectoryHit): string | undefined {
  const parts = [
    hit.organization?.name,
    hit.alias ? `по написанию «${hit.alias}»` : null,
    hit.state === 'draft' ? 'черновик' : hit.state === 'archived' ? 'в архиве' : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export interface SearchHit {
  id: string;
  label: string;
  /** Пояснение серым: почта, срок, автор и время. */
  sub?: string;
  /** Куда ведёт: карточка пользователя или раздел с тем же поиском. */
  link: NavigateOptions;
}

export interface SourceResult extends SourceInfo {
  hits: SearchHit[];
  /** Найдено больше, чем показано: «Все в разделе». */
  more: boolean;
  /** Запрос идёт. */
  pending: boolean;
  /** Сбой запроса — причина, код, номер операции; на время повтора — тот же. */
  failure: ErrorView | null;
  /** «Повторить» нажата, ответа ещё нет: сбой на месте, видна загрузка (T168). */
  retrying: boolean;
  retry: () => void;
  /** Раздел с тем же поиском: «Все в разделе «…»». */
  section: NavigateOptions;
}

/** «Не удалось найти замещения. Нет связи с сервисом… Код ошибки NET-001 · операция …». */
export function failureText(result: Pick<SourceResult, 'what' | 'failure'>): string {
  const view = result.failure;
  if (!view) return '';
  const operation = view.operation ? ` · операция ${formatOperation(view.operation)}` : '';
  return `Не удалось найти ${result.what}. ${view.message}. ${view.nextStep}. Код ошибки ${view.code}${operation}`;
}

/** Ответ списка: страница с курсором или весь список (документы, пункты, поиск справочника). */
interface Page<T> {
  items: T[];
  nextCursor?: string | null;
}

/**
 * Состояние запроса источника: forbidden — отказ ACC-* (источник скрыт), failure — другой сбой,
 * если показать нечего, и его повтор (useQueryFailure).
 */
function useSourceState<T>(query: UseQueryResult<Page<T>>, enabled: boolean) {
  const forbidden = isApiError(query.error) && isForbiddenCode(query.error.code);
  const { failure, retrying, retry } = useQueryFailure(query, { show: enabled && !forbidden });
  // Пока сбой на месте (и на время его повтора), варианты прежнего поиска не показываются.
  const shown = enabled && !forbidden && failure === null;
  return {
    items: shown ? (query.data?.items ?? []) : [],
    more: shown && Boolean(query.data?.nextCursor),
    pending: enabled && query.isFetching,
    failure,
    retrying,
    forbidden,
    retry,
  };
}

/**
 * Найденное по q во всех источниках, доступных ролям, — в порядке searchSources. enabled = false —
 * запросов нет (палитра закрыта). limit — сколько строк в группе.
 */
export function useSearchResults(
  q: string,
  { limit, enabled }: { limit: number; enabled: boolean },
): SourceResult[] {
  const me = useMe();
  const timeZone = useTimeZone();
  const allowed = new Set(allowedSources(me.permissions).map((source) => source.id));
  const ready = enabled && q.length >= SEARCH_FROM;
  const on = (id: SearchSourceId) => ready && allowed.has(id);

  const users = useQuery({
    ...apiQuery('users.list', { query: { q, limit } }),
    enabled: on('users'),
    placeholderData: keepPreviousData,
  });
  const substitutions = useQuery({
    ...apiQuery('substitutions.list', { query: { q, limit } }),
    enabled: on('substitutions'),
    placeholderData: keepPreviousData,
  });
  const grants = useQuery({
    ...apiQuery('supportAccess.list', { query: { q, limit } }),
    enabled: on('support'),
    placeholderData: keepPreviousData,
  });
  const audit = useQuery({
    ...apiQuery('audit.list', { query: { q, limit } }),
    enabled: on('audit'),
    placeholderData: keepPreviousData,
  });
  // Протоколов немного — их ищут в списке документов, без своего запроса поиска.
  const documents = useQuery({ ...apiQuery('documents.list'), enabled: on('documents') });
  const items = useQuery({
    ...apiQuery('control.items', { query: { q } }),
    enabled: on('items'),
    placeholderData: keepPreviousData,
  });
  const records = useQuery({
    ...apiQuery('directory.search', {
      query: { q, limit: Math.min(limit * 2, DIRECTORY_SEARCH_LIMIT) },
    }),
    enabled: on('people') || on('organizations'),
    placeholderData: keepPreviousData,
  });
  const usersState = useSourceState(users, on('users'));
  const substitutionsState = useSourceState(substitutions, on('substitutions'));
  const grantsState = useSourceState(grants, on('support'));
  const auditState = useSourceState(audit, on('audit'));
  const documentsState = useSourceState(documents, on('documents'));
  const itemsState = useSourceState(items, on('items'));
  const peopleState = useSourceState(records, on('people'));
  const organizationsState = useSourceState(records, on('organizations'));

  const foundDocuments = documentsState.items.filter((row) => matchesDocument(row, q));
  const recordsOf = (kind: RecordKind, state: typeof peopleState) =>
    state.items.filter((hit) => hit.record.kind === kind);
  const foundPeople = recordsOf('person', peopleState);
  const foundOrganizations = recordsOf('organization', organizationsState);
  const directoryHits = (found: DirectoryHit[]) =>
    found.slice(0, limit).map((hit) => {
      const sub = directoryHitSub(hit);
      return {
        id: `record:${hit.record.id}`,
        label: hit.record.name,
        ...(sub ? { sub } : {}),
        link: {
          to: '/directory' as const,
          search: { kind: hit.record.kind, record: hit.record.id },
        },
      };
    });

  const results: Record<
    SearchSourceId,
    Omit<SourceResult, keyof SourceInfo> & { forbidden: boolean }
  > = {
    documents: {
      ...documentsState,
      more: foundDocuments.length > limit,
      hits: foundDocuments.slice(0, limit).map((row) => ({
        id: `document:${row.id}`,
        label: `П-${row.number} · ${row.title}`,
        sub: row.latest ? `лист ${row.latest.sheetName}` : 'создан в системе',
        link: { to: '/control', search: { documentId: row.id } },
      })),
      section: { to: '/documents' },
    },
    people: {
      ...peopleState,
      more: foundPeople.length > limit,
      hits: directoryHits(foundPeople),
      section: { to: '/directory', search: { kind: 'person', q } },
    },
    organizations: {
      ...organizationsState,
      more: foundOrganizations.length > limit,
      hits: directoryHits(foundOrganizations),
      section: { to: '/directory', search: { kind: 'organization', q } },
    },
    items: {
      ...itemsState,
      more: itemsState.items.length > limit,
      hits: itemsState.items.slice(0, limit).map((item) => ({
        id: `item:${item.versionId}:${item.row}`,
        label: item.text,
        sub: itemSource(item),
        link: {
          to: '/control',
          search: {
            q: item.text.trim().slice(0, CONTROL_SEARCH_MAX).trim(),
            documentId: item.documentId,
          },
        },
      })),
      section: { to: '/control', search: { q } },
    },
    users: {
      ...usersState,
      hits: usersState.items.map((user) => ({
        id: `user:${user.id}`,
        label: user.fullName,
        sub: user.email,
        link: { to: '/users/$id', params: { id: user.id } },
      })),
      section: { to: '/users', search: { q } },
    },
    substitutions: {
      ...substitutionsState,
      hits: substitutionsState.items.map((item) => ({
        id: `substitution:${item.id}`,
        label: `Замещение: ${roleInfo[item.role].inText}, ${item.deputy.fullName}`,
        sub: `роль: ${item.owner.fullName} · ${formatDate(item.validFrom)} — ${formatDate(item.validTo)} · ${substitutionStateLabels[item.state]}`,
        link: { to: '/substitutions', search: { q } },
      })),
      section: { to: '/substitutions', search: { q } },
    },
    support: {
      ...grantsState,
      hits: grantsState.items.map((grant) => ({
        id: `support:${grant.id}`,
        label: `Доступ: ${grant.supportUser.fullName}`,
        sub: `выдан ${formatDateTime(grant.validFrom, timeZone)} · ${supportGrantStateLabels[grant.state]}`,
        link: { to: '/support-access', search: { q } },
      })),
      section: { to: '/support-access', search: { q } },
    },
    audit: {
      ...auditState,
      hits: auditState.items.map((entry) => ({
        id: `audit:${entry.id}`,
        label: `${auditOperationLabel(entry.operation)}: ${entry.entity.title ?? entityTypeLabel(entry.entity.type)}`,
        sub: `${auditActorName(entry.actor)} · ${formatDateTime(entry.at, timeZone)}`,
        link: { to: '/audit', search: { q } },
      })),
      section: { to: '/audit', search: { q } },
    },
  };

  return searchSources
    .filter((source) => allowed.has(source.id) && !results[source.id].forbidden)
    .map((source) => {
      const { forbidden: _forbidden, ...result } = results[source.id];
      return { ...source, ...result };
    });
}
